import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List as VirtualizedList } from 'react-virtualized';
import 'react-virtualized/styles.css';

import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { makeStyles } from 'tss-react/mui';
import { AuthContext } from "../../context/Auth/AuthContext";
import socket from "../../hooks/useSocket";
import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import SharedTicketsList from "../SharedTicketsList";
import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import "./styleTicketList.css";

const useStyles = makeStyles()((theme) => {
	return {
		ticketsListWrapper: {
			position: "relative",
			display: "flex",
			height: "100%",
			width: "100%",
			minHeight: 0, // Necessário para Firefox
			flexDirection: "column",
			overflow: "hidden",
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0,
		},

		ticketsList: {
			flex: 1,
			minHeight: 0, // Necessário para Firefox
			minWidth: 0, // Necessário para Firefox - permite flex shrink
			display: "flex",
			flexDirection: "column",
			overflow: "hidden",
			width: "100%",
			...theme.scrollbarStyles,
			borderTop: "2px solid rgba(0, 0, 0, 0.12)",
		},

		ticketsListHeader: {
			zIndex: 2,
			borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
		},

		ticketsCount: {
			fontWeight: "normal",
			color: "rgb(104, 121, 146)",
			marginLeft: "8px",
			fontSize: "14px",
		},

		noTicketsText: {
			textAlign: "center",
			color: "rgb(104, 121, 146)",
			fontSize: "14px",
			lineHeight: "1.4",
		},

		noTicketsTitle: {
			textAlign: "center",
			fontSize: "16px",
			fontWeight: "600",
			margin: "0px",
		},

		noTicketsDiv: {
			display: "flex",
			height: "100px",
			margin: 40,
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
		},

		selectionHeader: {
			display: "flex",
			alignItems: "center",
			padding: "8px 16px",
			borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
			backgroundColor: theme.palette.background.default,
			position: "sticky",
			top: 0,
			zIndex: 1,
		},

		virtualizedListContainer: {
			flex: "1 1 auto",
			minHeight: 0, // Necessário para Firefox
			minWidth: 0, // Necessário para Firefox - permite flex shrink
			height: "100%",
			width: "100%",
			overflow: "hidden",
			position: "relative",
			// Firefox specific - garante que o container use largura total
			display: "flex",
			flexDirection: "column",
		},
	}
});

// Função utilitária para ordenar tickets por fixed e fixedOrder
const sortTicketsByFixed = (tickets, status, ticketsOrder = "default", showAll = false, userTickets = null) => {
	if (!tickets || tickets.length === 0) return [];

	// ✅ CONDIÇÃO CORRETA: Só aplicar ordenação por fixed se status === "open" && !(showAll || userTickets === 'all')
	if (!(status === "open" && !(showAll || userTickets === 'all'))) {
		return tickets; // Não aplicar ordenação por fixed
	}

	// Separar tickets fixados e não fixados
	const fixedTickets = tickets.filter(ticket => ticket.fixed === true);
	const regularTickets = tickets.filter(ticket => ticket.fixed !== true);

	// Ordenar tickets fixados por fixedOrder (0, 1, 2, ...)
	const sortedFixedTickets = fixedTickets.sort((a, b) => {
		const orderA = a.fixedOrder !== null && a.fixedOrder !== undefined ? a.fixedOrder : Infinity;
		const orderB = b.fixedOrder !== null && b.fixedOrder !== undefined ? b.fixedOrder : Infinity;
		return orderA - orderB;
	});

	// Para tickets regulares, manter a ordem que vem do ticketsOrder
	// (não reordenar, manter a sequência original)

	// Retornar tickets fixados primeiro, depois os regulares na ordem original
	return [...sortedFixedTickets, ...regularTickets];
};


const ticketsReducer = (state, action) => {
	const { type, payload, status: statusTab, ticketsOrder = "default" } = action;

	switch (type) {
		case "LOAD_TICKETS": {
			const newTickets = payload;
			const ticketMap = new Map(state.map(ticket => [ticket.id, ticket]));

			// Atualizar tickets existentes e adicionar novos
			newTickets.forEach(ticket => {
				const existingTicket = ticketMap.get(ticket.id);
				if (existingTicket) {
					// Atualizar ticket existente
					ticketMap.set(ticket.id, ticket);
				} else {
					// Adicionar novo ticket
					ticketMap.set(ticket.id, ticket);
				}
			});

			const result = Array.from(ticketMap.values());

			// ✅ CONDIÇÃO CORRETA: Aplicar ordenação por fixed apenas se status === "open" && !(showAll || userTickets === 'all')
			if (statusTab === "open" && !(action.showAll || action.userTickets === 'all')) {
				const sortedByFixed = sortTicketsByFixed(result, statusTab, ticketsOrder, action.showAll, action.userTickets);

				// Para status "open", separar por fixed e depois por unread dentro de cada categoria
				const fixedTickets = sortedByFixed.filter(t => t.fixed === true);
				const regularTickets = sortedByFixed.filter(t => t.fixed !== true);

				const fixedUnread = fixedTickets.filter(t => t.unreadMessages > 0);
				const fixedRead = fixedTickets.filter(t => t.unreadMessages === 0);
				const regularUnread = regularTickets.filter(t => t.unreadMessages > 0);
				const regularRead = regularTickets.filter(t => t.unreadMessages === 0);

				return [...fixedUnread, ...fixedRead, ...regularUnread, ...regularRead];
			} else {
				// Para outros casos, apenas ordenar por unread
				const unreadTickets = result.filter(t => t.unreadMessages > 0);
				const readTickets = result.filter(t => t.unreadMessages === 0);
				return [...unreadTickets, ...readTickets];
			}
		}

		case "RESET_UNREAD": {
			const newState = state.map(ticket =>
				ticket.id === payload
					? { ...ticket, unreadMessages: 0 }
					: ticket
			);
			// ✅ CONDIÇÃO CORRETA: Reordenar apenas se status === "open" && !(showAll || userTickets === 'all')
			return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
				? sortTicketsByFixed(newState, statusTab, action.ticketsOrder, action.showAll, action.userTickets)
				: newState;
		}

		case "UPDATE_TICKET": {
			const ticket = payload;
			if (ticket.status !== statusTab) return state;

			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				const newState = [...state];
				newState[ticketIndex] = ticket;
				// ✅ CONDIÇÃO CORRETA: Reordenar apenas se status === "open" && !(showAll || userTickets === 'all')
				return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
					? sortTicketsByFixed(newState, statusTab, action.ticketsOrder, action.showAll, action.userTickets)
					: newState;
			}
			const newState = [ticket, ...state];
			return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
				? sortTicketsByFixed(newState, statusTab, action.ticketsOrder, action.showAll, action.userTickets)
				: newState;
		}

		case "UPDATE_TICKET_UNREAD_MESSAGES": {
			const ticket = payload;
			const ticketIndex = state.findIndex(t => t.id === ticket.id);

			if (ticketIndex !== -1) {
				if (statusTab && statusTab !== ticket.status) {
					return state.filter((_, index) => index !== ticketIndex);
				}

				const newState = [...state];
				newState[ticketIndex] = ticket;

				// Reordenar baseado no status e ordem configurada
				if (ticket.status === 'pending') {
					const updatedTicket = newState.splice(ticketIndex, 1)[0];
					const reorderedState = ticketsOrder === 'asc'
						? [updatedTicket, ...newState]
						: [...newState, updatedTicket];
					return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
						? sortTicketsByFixed(reorderedState, statusTab, ticketsOrder, action.showAll, action.userTickets)
						: reorderedState;
				}

				return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
					? sortTicketsByFixed(newState, statusTab, ticketsOrder, action.showAll, action.userTickets)
					: newState;
			}

			// Adicionar novo ticket
			if (ticket.status === 'pending') {
				const newState = ticketsOrder === 'asc'
					? [...state, ticket]
					: [ticket, ...state];
				return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
					? sortTicketsByFixed(newState, statusTab, ticketsOrder, action.showAll, action.userTickets)
					: newState;
			}

			if (statusTab && statusTab === ticket.status) {
				const newState = [ticket, ...state];
				return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
					? sortTicketsByFixed(newState, statusTab, ticketsOrder, action.showAll, action.userTickets)
					: newState;
			}

			return state;
		}

		case "UPDATE_TICKET_CONTACT": {
			const contact = payload;
			const newState = state.map(ticket =>
				+ticket.contactId === +contact.id
					? {
						...ticket,
						contact: {
							...ticket.contact,
							...contact,
						}
					}
					: ticket
			);
			return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
				? sortTicketsByFixed(newState, statusTab, action.ticketsOrder, action.showAll, action.userTickets)
				: newState;
		}

		case "DELETE_TICKET": {
			const newState = state.filter(ticket => ticket.id !== payload);
			return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
				? sortTicketsByFixed(newState, statusTab, action.ticketsOrder, action.showAll, action.userTickets)
				: newState;
		}

		case "REORDER_BY_FIXED": {
			// ✅ CONDIÇÃO CORRETA: Ação específica para reordenar quando fixed muda
			return (statusTab === "open" && !(action.showAll || action.userTickets === 'all'))
				? sortTicketsByFixed([...state], statusTab, action.ticketsOrder, action.showAll, action.userTickets)
				: state;
		}

		case "RESET": {
			return [];
		}

		default:
			return state;
	}
};


const TicketsList = ({
	noQueue,
	companyId,
	status,
	viewTags,
	viewConnections,
	searchParam,
	showAll,
	selectedQueueIds = [],
	selectedConnectionIds = [],
	userTickets,
	reloadCount,
	onTicketSelection,
	selectedTickets = [],
	isRescheduled = false,
}) => {
	const { classes } = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(ticketsReducer, []);
	const { user } = useContext(AuthContext);
	const [configs, setConfigs] = useState({ ticketsOrder: "default" });

	const previousFixedStateRef = useRef(new Map());
	const lastSetPageRef = useRef(0); // Ref para evitar múltiplas chamadas de página em curto período

	// Cache profissional para medição de altura dinâmica dos itens
	const cellMeasurerCacheRef = useRef(
		new CellMeasurerCache({
			fixedWidth: true,
			defaultHeight: 100,
			minHeight: 60  // Altura mínima para evitar colapso
		})
	);

	const listRef = useRef(null);

	// Tracking de mudanças para invalidação inteligente de cache
	const ticketContentHashRef = useRef(new Map());

	// Funções para seleção múltipla
	const handleTicketToggle = useCallback((ticketId) => {
		const newSelected = selectedTickets.includes(ticketId)
			? selectedTickets.filter(id => id !== ticketId)
			: [...selectedTickets, ticketId];

		onTicketSelection?.(newSelected);
	}, [selectedTickets, onTicketSelection]);

	// Memoizar valores derivados do usuário
	const userProfile = useMemo(() => user?.profile, [user?.profile]);
	const userQueues = useMemo(() => user?.queues || [], [user?.queues]);
	const userQueueIds = useMemo(() => userQueues.map(q => q.id), [userQueues]);
	const userId = useMemo(() => user?.id, [user?.id]);

	// Memoizar parâmetros para useTickets
	const ticketsParams = useMemo(() => ({
		pageNumber,
		searchParam,
		// Quando isRescheduled, o backend filtra por isRescheduled=true ignorando
		// o status — mas enviamos "pending" para passar pela validação da rota.
		status: isRescheduled ? "pending" : status,
		userId: userTickets !== 'all' ? userTickets : userId,
		showAll: showAll || userTickets === 'all',
		queueIds: JSON.stringify(selectedQueueIds),
		connectionsIds: JSON.stringify(selectedConnectionIds),
		isRescheduled: isRescheduled || undefined,
	}), [pageNumber, searchParam, status, isRescheduled, userTickets, userId, showAll, selectedQueueIds, selectedConnectionIds]);

	const { tickets, hasMore, loading, count } = useTickets(ticketsParams);

	// Reset quando filtros mudarem (incluindo showAll e userTickets que afetam ordenação)
	const resetDeps = useMemo(() =>
		[status, searchParam, showAll, userTickets, selectedQueueIds, selectedConnectionIds].join('|'),
		[status, searchParam, showAll, userTickets, selectedQueueIds, selectedConnectionIds]
	);

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
		// Limpar referência de fixed state quando filtros mudarem
		previousFixedStateRef.current = new Map();
		// Limpar cache de medições quando filtros mudarem
		cellMeasurerCacheRef.current.clearAll();
		if (listRef.current) {
			listRef.current.recomputeRowHeights();
		}
	}, [resetDeps]);

	// Buscar configurações da empresa (otimizado)
	useEffect(() => {
		if (!companyId) return;

		let isMounted = true;

		const fetchConfigs = async () => {
			try {
				const { data } = await api.get(`/company/${companyId}`);
				if (isMounted) {
					setConfigs(data);
				}
			} catch (err) {
				console.error('Erro ao buscar configurações:', err);
			}
		};

		fetchConfigs();

		return () => {
			isMounted = false;
		};
	}, [companyId]);


	// Lógica de filtragem otimizada
	const filteredTickets = useMemo(() => {
		if (!tickets.length) return [];

		let filtered = tickets;

		// Filtrar por filas do usuário baseado no perfil
		if (!noQueue) {
			switch (userProfile) {
				case "user":
					filtered = filtered.filter(t => userQueueIds.includes(t.queueId));
					break;
				case "supervisor":
					filtered = filtered.filter(t => userQueueIds.includes(t.queueId) || !t.queueId);
					break;
				default:
					// Admin vê todos
					break;
			}
		}

		// Filtrar por usuário específico
		if (userTickets && userTickets !== 'all') {
			if (isRescheduled) {
				// Tickets reagendados têm userId=null e queueId=null após CreateRescheduleTicketService.
				// O backend já filtra por companyId e isRescheduled=true — não aplicar filtros
				// adicionais de userId/queueId no frontend para não excluir todos os tickets.
			} else if (status === 'pending') {
				filtered = userProfile === "user" && !noQueue
					? filtered.filter(t => selectedQueueIds.includes(t.queueId))
					: filtered;
			} else {
				filtered = showAll || userTickets === 'all'
					? filtered
					: filtered.filter(t => t?.userId != null && t?.userId == userTickets);
			}
		}

		return filtered;
	}, [tickets, noQueue, userProfile, userQueueIds, userTickets, status, selectedQueueIds, showAll]);

	// Carregar tickets no reducer
	useEffect(() => {
		if (!status && !searchParam) return;

		dispatch({
			type: "LOAD_TICKETS",
			payload: filteredTickets,
			ticketsOrder: configs.ticketsOrder,
			status,
			showAll,
			userTickets
		});
	}, [filteredTickets, status, searchParam, configs.ticketsOrder, showAll, userTickets]);

	// ====================================
	// NOVO: Detectar mudanças em 'fixed' e reordenar
	// ====================================
	useEffect(() => {
		// ✅ CONDIÇÃO CORRETA: Aplicar lógica apenas se status === "open" && !(showAll || userTickets === 'all')
		if (!(status === "open" && !(showAll || userTickets === 'all')) || !ticketsList || ticketsList.length === 0) return;

		// Criar mapa atual do estado fixed dos tickets
		const currentFixedState = new Map();
		ticketsList.forEach(ticket => {
			currentFixedState.set(ticket.id, {
				fixed: ticket.fixed,
				fixedOrder: ticket.fixedOrder
			});
		});

		const previousFixedState = previousFixedStateRef.current;
		let hasFixedChanges = false;

		// Verificar se houve mudanças no estado 'fixed' ou 'fixedOrder'
		if (previousFixedState.size > 0) {
			for (const [ticketId, currentState] of currentFixedState) {
				const previousState = previousFixedState.get(ticketId);

				if (!previousState ||
					previousState.fixed !== currentState.fixed ||
					previousState.fixedOrder !== currentState.fixedOrder) {
					hasFixedChanges = true;
					break;
				}
			}

			// Verificar se algum ticket foi removido
			if (!hasFixedChanges) {
				for (const ticketId of previousFixedState.keys()) {
					if (!currentFixedState.has(ticketId)) {
						hasFixedChanges = true;
						break;
					}
				}
			}
		} else if (currentFixedState.size > 0) {
			// Primeira vez que carrega tickets com condições válidas
			hasFixedChanges = true;
		}

		// Se houve mudanças, reordenar a lista
		if (hasFixedChanges) {
			console.log('🔄 Detectada mudança em tickets fixados (status: open && !(showAll || userTickets === all)), reordenando lista...');
			dispatch({
				type: "REORDER_BY_FIXED",
				status: status,
				ticketsOrder: configs.ticketsOrder,
				showAll,
				userTickets
			});
		}

		// Atualizar referência do estado anterior
		previousFixedStateRef.current = currentFixedState;

	}, [ticketsList, status, showAll, userTickets, configs.ticketsOrder]);



	// Configuração dos listeners do socket (otimizada)
	useEffect(() => {
		if (!companyId) return;

		const userCompany = companyId;

		// Funções auxiliares memoizadas
		const shouldUpdateTicket = (ticket) => {
			const userMatch = !ticket?.userId ||
				(userTickets !== "all" ? +userTickets === +ticket?.userId : +ticket?.userId === +userId) ||
				(showAll || userTickets === 'all');

			const queueMatch = !ticket?.queueId || selectedQueueIds?.includes(+ticket?.queueId);
			const connectionMatch = !ticket?.whatsappId || selectedConnectionIds?.includes(+ticket?.whatsappId);

			return userMatch && queueMatch && connectionMatch;
		};

		const notBelongsToUserQueues = (ticket) =>
			(ticket?.queueId && !selectedQueueIds?.includes(ticket?.queueId)) &&
			(!ticket?.whatsappId || selectedConnectionIds?.includes(ticket?.whatsappId));

		// Event handlers
		const handleConnect = () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		};

		const handleTicketUpdate = (data) => {
			const baseAction = {
				ticketsOrder: data?.findCompany?.ticketsOrder || configs.ticketsOrder,
				status,
				showAll,
				userTickets
			};

			switch (data.action) {
				case "updateUnread":
					dispatch({
						type: "RESET_UNREAD",
						payload: data.ticketId,
						...baseAction,
					});
					break;

				case "update":
					if (shouldUpdateTicket(data.ticket)) {
						dispatch({
							type: "UPDATE_TICKET",
							payload: data.ticket,
							...baseAction,
						});
					} else if (notBelongsToUserQueues(data.ticket)) {
						dispatch({
							type: "DELETE_TICKET",
							payload: data.ticket.id,
							...baseAction,
						});
					}
					break;

				case "delete":
					dispatch({
						type: "DELETE_TICKET",
						payload: data.ticketId,
						status,
						ticketsOrder: configs.ticketsOrder,
						showAll,
						userTickets
					});
					break;
			}
		};

		const handleAppMessage = (data) => {
			if (userProfile === "user" &&
				(userQueueIds?.indexOf(data?.ticket?.queue?.id) === -1 || data?.ticket?.queue === null)) {
				return;
			}

			if (data?.action === "create" &&
				shouldUpdateTicket(data?.ticket) &&
				companyId == data?.ticket?.whatsapp?.companyId) {

				// Verificações de usuário
				const isUserTicketMismatch = userTickets &&
					userId !== data?.ticket?.userId &&
					((!showAll || userTickets !== 'all') && data?.ticket?.status !== "pending");

				const isSpecificUserMismatch = userTickets &&
					userTickets !== data?.ticket?.userId &&
					((!showAll || userTickets !== 'all') && data?.ticket?.status !== "pending");

				if (isUserTicketMismatch || isSpecificUserMismatch) {
					return;
				}

				const ticketAppMessage = {
					...data.ticket,
					lastMessage: data?.message?.body || data.ticket?.lastMessage || "",
					updatedAt: data?.message?.updatedAt || data.ticket?.updatedAt || new Date().toISOString(),
				};

				dispatch({
					type: "UPDATE_TICKET_UNREAD_MESSAGES",
					payload: ticketAppMessage,
					ticketsOrder: data?.findCompany?.ticketsOrder || configs.ticketsOrder,
					status,
					showAll,
					userTickets
				});
			}
		};

		const handleContact = (data) => {
			if (data.action === "update") {
				const filteredContacts = {
					...data.contact,
					status: data.status,
					createdContact: true,
				};

				dispatch({
					type: "UPDATE_TICKET_CONTACT",
					payload: filteredContacts,
					ticketsOrder: data?.findCompany?.ticketsOrder || configs.ticketsOrder,
					status,
					showAll,
					userTickets
				});
			}
		};

		// Registrar listeners
		socket.on("connect", handleConnect);
		socket.on(`ticket-${userCompany}`, handleTicketUpdate);
		socket.on(`appMessage-${userCompany}`, handleAppMessage);
		socket.on("contact", handleContact);

		// Cleanup: remove os 4 handlers ao desmontar/quando deps mudam.
		// Sem isso, cada troca de aba/filtro adicionava 4 listeners que
		// nunca eram removidos — cada um carregava o dispatch+state inteiro
		// da reducer de tickets em closure (vazamento gigante).
		return () => {
			// socket.off("connect", handleConnect);
			socket.off(`ticket-${userCompany}`, handleTicketUpdate);
			// socket.off(`appMessage-${userCompany}`, handleAppMessage);
			// socket.off("contact", handleContact);
		};
	}, [
		companyId, status, showAll, userTickets, selectedQueueIds, selectedConnectionIds,
		userProfile, userQueueIds, userId, configs.ticketsOrder
	]);

	// Aplicar filtros finais de forma otimizada (ordenação por fixed com condição correta)
	const finalFilteredTickets = useMemo(() => {
		let filtered = [...ticketsList];

		// Filtrar por noQueue
		if (!noQueue) {
			filtered = filtered.filter(ticket =>
				ticket.queueId !== null || ticket.userId === userTickets
			);
		}

		// Filtrar por status
		if (status) {
			filtered = filtered.filter(ticket => ticket.status === status);
		}

		// Filtrar por filas selecionadas
		if (selectedQueueIds.length > 0 && status) {
			filtered = filtered.filter(ticket =>
				selectedQueueIds.includes(ticket.queueId) || ticket.queueId === null
			);
		}

		// Filtrar por conexões selecionadas
		if (selectedConnectionIds.length > 0 && status) {
			filtered = filtered.filter(ticket =>
				selectedConnectionIds.includes(ticket.whatsappId) || ticket.whatsappId === null
			);
		}

		// Filtrar tickets abertos por usuário específico
		if (status === 'open' && userTickets !== 'all') {
			filtered = filtered.filter(ticket => ticket.userId == userTickets);
		}

		// ✅ CONDIÇÃO CORRETA: Aplicar ordenação final por fixed apenas se status === "open" && !(showAll || userTickets === 'all')
		return (status === "open" && !(showAll || userTickets === 'all'))
			? sortTicketsByFixed(filtered, status, configs.ticketsOrder, showAll, userTickets)
			: filtered;
	}, [ticketsList, noQueue, status, selectedQueueIds, selectedConnectionIds, userTickets, configs.ticketsOrder, showAll]);

	// Sistema de invalidação inteligente de cache quando conteúdo muda
	useEffect(() => {
		if (!finalFilteredTickets.length) return;

		let hasChanges = false;

		finalFilteredTickets.forEach((ticket, index) => {
			// Hash baseado em propriedades que afetam altura do item
			const contentHash = JSON.stringify({
				lastMessage: ticket.lastMessage,
				tagsCount: ticket.tags?.length || 0,
				status: ticket.status,
				unreadMessages: ticket.unreadMessages,
				fixed: ticket.fixed,
				userId: ticket.userId,
				userName: ticket.user?.name,
				isTyping: ticket.isTyping
			});

			const previousHash = ticketContentHashRef.current.get(ticket.id);

			// Se o conteúdo mudou, invalidar cache deste item
			if (previousHash && previousHash !== contentHash) {
				cellMeasurerCacheRef.current.clear(index, 0);
				hasChanges = true;
			}

			ticketContentHashRef.current.set(ticket.id, contentHash);
		});

		// Recomputar alturas apenas se houve mudanças
		if (hasChanges && listRef.current) {
			listRef.current.recomputeRowHeights();
		}
	}, [finalFilteredTickets]);

	// Limpar cache quando viewTags ou viewConnections mudam (afeta altura dos items)
	useEffect(() => {
		cellMeasurerCacheRef.current.clearAll();
		if (listRef.current) {
			listRef.current.recomputeRowHeights();
		}
	}, [viewTags, viewConnections]);

	// Função handleSelectAll - definida após finalFilteredTickets
	const handleSelectAll = useCallback(() => {
		if (selectedTickets.length === finalFilteredTickets.length) {
			onTicketSelection?.([]);
		} else {
			const allTicketIds = finalFilteredTickets.map(ticket => ticket.id);
			onTicketSelection?.(allTicketIds);
		}
	}, [selectedTickets, finalFilteredTickets, onTicketSelection]);

	// Resetar seleções quando showAll ou notMyUser for true
	useEffect(() => {
		if (showAll || userTickets === 'all' || (+userTickets !== +userId)) {
			onTicketSelection?.([]);
		}
	}, [showAll, userTickets, userId, onTicketSelection]);

	// Atualizar contador
	useEffect(() => {
		if (!status) return;
		const counter = count || finalFilteredTickets?.length || 0;
		reloadCount?.(counter);
	}, [finalFilteredTickets, status, reloadCount, count]);

	// Função otimizada para renderizar cada linha da lista virtualizada
	const rowRenderer = useCallback(({ index, key, style, parent }) => {
		const ticket = finalFilteredTickets[index];
		const cache = cellMeasurerCacheRef.current;

		// Estilo do container - usa 100% para garantir compatibilidade cross-browser
		// Mantém top/left/height do style original para posicionamento correto
		const containerStyle = {
			position: style.position,
			top: style.top,
			left: style.left,
			height: style.height,
			width: '100%', // Usa 100% em vez de style.width para Firefox
			boxSizing: 'border-box',
			overflow: 'hidden',
		};

		if (!ticket) {
			return (
				<CellMeasurer
					key={key}
					cache={cache}
					parent={parent}
					columnIndex={0}
					rowIndex={index}
				>
					<div style={containerStyle}>
						<TicketsListSkeleton />
					</div>
				</CellMeasurer>
			);
		}

		return (
			<CellMeasurer
				key={key}
				cache={cache}
				parent={parent}
				columnIndex={0}
				rowIndex={index}
			>
				{({ measure, registerChild }) => (
					<div style={containerStyle} ref={registerChild}>
						<TicketListItem
							showAll={showAll || userTickets === 'all'}
							notMyUser={+userTickets !== +userId}
							useTransfer={!(showAll || userTickets === 'all' || +userTickets !== +userId)}
							ticket={ticket}
							viewTags={viewTags}
							viewConnections={viewConnections}
							selectable={onTicketSelection && status === 'open' &&
								!showAll && !(userTickets === 'all') && (+userTickets === +userId)}
							selected={selectedTickets.includes(ticket.id)}
							onToggleSelect={() => handleTicketToggle(ticket.id)}
							onContentChange={measure}
						/>
					</div>
				)}
			</CellMeasurer>
		);
	}, [finalFilteredTickets, showAll, userTickets, userId, viewTags, viewConnections,
		onTicketSelection, status, selectedTickets, handleTicketToggle]);


	// Scroll handler otimizado sem logs desnecessários
	const handleListScroll = useCallback(({ clientHeight, scrollHeight, scrollTop }) => {
		if (loading || !hasMore) return;

		const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

		// Throttle de 0,7 segundos
		const now = Date.now();
		if (now - lastSetPageRef.current < 700) {
			return;
		}

		if (distanceFromBottom < 200) {
			lastSetPageRef.current = now;
			setPageNumber((prev) => prev + 1);
		}
	}, [loading, hasMore]);

	return (
		<div className={classes.ticketsListWrapper}>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
			>
				<SharedTicketsList
					status={status}
					showAll={showAll}
					userTickets={userTickets}
					viewTags={viewTags}
					viewConnections={viewConnections}
					userId={userId}
				/>

				{/* Cabeçalho de seleção múltipla - apenas para tickets com status open e apenas meus tickets */}
				{onTicketSelection && status === 'open' && finalFilteredTickets.length > 0 &&
					!showAll && !(userTickets === 'all') && (+userTickets === +userId) && (
						<Box className={classes.selectionHeader}>
							<Checkbox
								indeterminate={selectedTickets.length > 0 && selectedTickets.length < finalFilteredTickets.length}
								checked={finalFilteredTickets.length > 0 && selectedTickets.length === finalFilteredTickets.length}
								onChange={handleSelectAll}
								color="primary"
							/>
							<Typography variant="body2" sx={{ ml: 1 }}>
								{selectedTickets.length > 0
									? `${selectedTickets.length} selecionado(s)`
									: 'Selecionar todos'
								}
							</Typography>
						</Box>
					)}

				{/* Lista virtualizada com react-virtualized */}
				{finalFilteredTickets?.length === 0 && !loading ? (
					<div className={classes.noTicketsDiv}>
						<span className={classes.noTicketsTitle}>
							{i18n.t("ticketsList.noTicketsTitle")}
						</span>
						<p className={classes.noTicketsText}>
							{i18n.t("ticketsList.noTicketsMessage")}
						</p>
					</div>
				) : (
					<div className={classes.virtualizedListContainer}>
						<AutoSizer style={{ width: '100%', height: '100%', flex: 1 }}>
							{({ height, width }) => (
								<VirtualizedList
									ref={listRef}
									height={height || 400}
									width={width || 300}
									rowCount={finalFilteredTickets.length}
									deferredMeasurementCache={cellMeasurerCacheRef.current}
									rowHeight={cellMeasurerCacheRef.current.rowHeight}
									rowRenderer={rowRenderer}
									overscanRowCount={3}
									onScroll={handleListScroll}
									scrollToAlignment="auto"
									estimatedRowSize={100}
								/>
							)}
						</AutoSizer>
					</div>
				)}

				{loading && <TicketsListSkeleton />}
			</Paper>
		</div>
	);
};

export default React.memo(TicketsList);